import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Arrow } from "../../../assets/icons/sortArrow.svg";
import LoaderRing from "../loaderRing/LoaderRing";
import {
  compare,
  calcTrends,
  getPlayerTrends,
  calcTrendWithDirection,
  formattedValue,
  getArrowStyle,
  RowsName,
} from "components/marketpleace/Products/utils/helpers";
import { PreferredPosition } from "types/userTypes";
import { IAvgStats, IPlayerStats, IPlayerTrendData } from "types/homeTable";
import { useViewportWidth } from "hooks/useViewportWidth";
import {
  EnduraceInfo,
  IntensityInfo,
  DynamicInfo,
  HrrInfo,
  HeartRateInfo,
  PowerInfo,
  PizInfo,
  HMLDInfo,
  TTSInfo,
  SprintInfo,
  HSRInfo,
  RunInfo,
  DistanceInfo,
  VMAXInfo,
  TasksInfo,
  PotencialInfo,
  ReadinessInfo,
  FormInfo,
  getStyledSpan,
  TrendStyledSpan,
  renderTrendSpan,
  flexEndClass,
  stickyThStyle,
  flexClass,
  arrowStyle,
  mainTableHeaderBgColor,
  academyStatsRows,
  headerColOddBgColor,
  headerColEvenBgColor,
  headerSortOddUnitBgColor,
  headerSortEvenUnitBgColor,
  getStyledSpanWithProgressbar,
  CheckboxKey,
  CheckboxesState,
  DEFAULT_HOME_COLUMN_ORDER,
} from "./HomeInfoHelper";
import { InfoBubble } from "./InfoBubblePortal";
import { formatTimelineTimeOnField, PlayerTimelineDiary } from "./HomeTableTimelineSummary";
import "./table.css";

interface Props {
  playerProfiles: IPlayerStats[];
  avgStats: IAvgStats;
  loading: boolean;
  searchQuery: string;
  mainAcademyName: string;
  isTrendChecked: boolean;
  trendStats?: IPlayerTrendData;

  homeColsCheckboxes: CheckboxesState;
  homeColumnOrder: CheckboxKey[];
  academyFolder: any;
}

const ORDERED_TABLE_COLUMN_KEYS = DEFAULT_HOME_COLUMN_ORDER.filter((key) => key !== "klub");

const SOURCE_TABLE_COLUMN_KEYS: CheckboxKey[] = [
  "poz",
  "lat",
  "phv",
  "dyspozycja",
  "zadan",
  "czas",
  "dziennik",
  "vmax",
  "dystans",
  "bieg",
  "hsr",
  "sprint",
  "tts",
  "hmld",
  "accDec",
  "moc",
  "tetno",
  "kalorie",
  "playerLoad",
];

interface OrderedCellsProps {
  keys: CheckboxKey[];
  order: CheckboxKey[];
  sourceKeys: CheckboxKey[];
  visibility: CheckboxesState;
  duplicateKeys?: CheckboxKey[];
  children: ReactNode;
}

const flattenOrderedCells = (children: ReactNode): ReactNode[] =>
  Children.toArray(children).flatMap((child) =>
    isValidElement(child) && child.type === Fragment
      ? flattenOrderedCells(child.props.children)
      : [child]
  );

const OrderedCells: React.FC<OrderedCellsProps> = ({
  keys,
  order,
  sourceKeys,
  visibility,
  duplicateKeys = [],
  children,
}) => {
  const renderedChildren = flattenOrderedCells(children);
  const activeKeys = sourceKeys.filter((key) => visibility[key].active);
  const cellsByKey = new Map<CheckboxKey, ReactNode>();
  let childIndex = 0;

  activeKeys.forEach((key) => {
    // Sprint is rendered as one value cell plus two alignment cells.
    const childCount = duplicateKeys.includes(key) ? 3 : 1;
    const columnChildren = renderedChildren.slice(childIndex, childIndex + childCount);
    const keyedColumnChildren = columnChildren.map((child, index) =>
      isValidElement(child) ? cloneElement(child, { key: `${key}-${index}` }) : child
    );

    cellsByKey.set(
      key,
      childCount === 1 ? keyedColumnChildren[0] : keyedColumnChildren
    );
    childIndex += childCount;
  });

  return (
    <>
      {order
        .filter((key) => keys.includes(key))
        .map((key) => (
          <Fragment key={key}>{cellsByKey.get(key) ?? null}</Fragment>
        ))}
    </>
  );
};

const SORT_FIELD_BY_KEY: Record<string, string> = {
  poz: "poz",
  Age: "lat",
  PHV: "phv",
  forma: "forma",
  gotowość: "gotowość",
  potencjał: "potencjał",
  "task number": "taskNumber",
  "task min": "taskMin",
  VMAX: "VMAX",
  "distance total": "distanceTotal",
  "distance Avg": "distanceAvg",
  "run m": "runM",
  "run %": "run%",
  "hsr m": "hsr",
  "hsr percentage": "hsrPercentage",
  "sprint m": "sprint",
  "sprint %": "sprint%",
  "sprint L": "sprintL",
  tts: "tts",
  "hmld m": "hmldM",
  "hmld percentage": "hmldPercentage",
  "piz acceleration": "pizAcceleration",
  "piz deceleration": "pizDeceleration",
  "power max": "power",
  "Max HeartRate": "MaxHeartRate",
  "Average HeartRate": "AverageHeartRate",
  hr1: "hr1",
  hr2: "hr2",
  hr3: "hr3",
  hr4: "hr4",
  hr5: "hr5",
  kalorie: "kalorie",
  playerLoad: "playerLoad",
  dynamika: "dynamika",
  intensity: "intensity",
  wytrzymałość: "wytrzymałość",
};

const HomeTableNew: React.FC<Props> = ({
  playerProfiles,
  avgStats,
  loading,
  searchQuery,
  mainAcademyName,
  isTrendChecked,
  trendStats,
  homeColsCheckboxes,
  homeColumnOrder,
  academyFolder,
}) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "asc" | "desc";
  }>({
    key: "",
    order: "desc",
  });
  const [isSortAnimating, setIsSortAnimating] = useState(false);
  const sortAnimTimeoutRef = useRef<number | null>(null);
  const sortAnimFrameRef = useRef<number | null>(null);

  const sortField = SORT_FIELD_BY_KEY[sortConfig.key] ?? "";
  const sortDirection = sortConfig.order;

  const [scrollX, setScrollX] = useState(() => {
    const val = parseInt(localStorage.getItem("scrollPosition") ?? "0", 10);
    return isNaN(val) ? 0 : val;
  });

  useEffect(() => {
    return () => {
      if (sortAnimTimeoutRef.current !== null) {
        window.clearTimeout(sortAnimTimeoutRef.current);
      }
      if (sortAnimFrameRef.current !== null) {
        window.cancelAnimationFrame(sortAnimFrameRef.current);
      }
    };
  }, []);

  const filteredItems = useMemo(() => {
    const toSortableNumber = (value: unknown): number => {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }

      if (value == null) {
        return 0;
      }

      const normalized = String(value).replace(/\s+/g, "").replace(",", ".");
      const parsed = parseFloat(normalized);

      return Number.isFinite(parsed) ? parsed : 0;
    };

    const str = searchQuery.toLowerCase();

    const filtered = !searchQuery.length
      ? playerProfiles
      : playerProfiles.filter(
          (player: any) =>
            player.LastName?.toLowerCase().includes(str) ||
            player.FirstName?.toLowerCase().includes(str)
        );

    if (!sortConfig.key) {
      return filtered
        .slice()
        .sort((a: any, b: any) => (a.LastName ?? "").localeCompare(b.LastName ?? ""));
    }

    const sortKey = RowsName[sortConfig.key];
    if (!sortKey) return filtered;

    return filtered.slice().sort((a: any, b: any) => {
      const aVal =
        sortConfig.key === "distance Avg"
          ? toSortableNumber(a?.TotalDistance) /
            Math.max(toSortableNumber(a?.EventTotalTimeInMinutes), 1)
          : toSortableNumber(a?.[sortKey]);
      const bVal =
        sortConfig.key === "distance Avg"
          ? toSortableNumber(b?.TotalDistance) /
            Math.max(toSortableNumber(b?.EventTotalTimeInMinutes), 1)
          : toSortableNumber(b?.[sortKey]);

      if (sortConfig.order === "asc") {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
  }, [playerProfiles, searchQuery, sortConfig]);

  const filterBy = (primaryValue: string, secondaryValue?: string) => {
    let key = primaryValue;

    if (secondaryValue) {
      key = `${primaryValue} ${secondaryValue}`;
    }

    setSortConfig((prevSortConfig) => ({
      key,
      order: prevSortConfig.key === key && prevSortConfig.order === "desc" ? "asc" : "desc",
    }));

    setIsSortAnimating(false);
    if (sortAnimFrameRef.current !== null) {
      window.cancelAnimationFrame(sortAnimFrameRef.current);
    }
    sortAnimFrameRef.current = window.requestAnimationFrame(() => {
      setIsSortAnimating(true);
      if (sortAnimTimeoutRef.current !== null) {
        window.clearTimeout(sortAnimTimeoutRef.current);
      }
      sortAnimTimeoutRef.current = window.setTimeout(() => {
        setIsSortAnimating(false);
      }, 260);
    });
  };

  const toggleSort = (_field: string) => undefined;

  const currentMonitorView = useViewportWidth();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isHorizontallyOverflowing, setIsHorizontallyOverflowing] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const val = parseInt(localStorage.getItem("scrollPosition") ?? "0", 10);
      if (!isNaN(val) && val !== scrollX) {
        setScrollX(val);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [scrollX]);

  useEffect(() => {
    const findHorizontalScrollHost = (start: HTMLElement | null) => {
      let el = start?.parentElement ?? null;

      while (el) {
        const style = window.getComputedStyle(el);
        const canScrollX =
          style.overflowX === "auto" ||
          style.overflowX === "scroll" ||
          el.scrollWidth > el.clientWidth + 1;

        if (canScrollX) {
          return el;
        }

        el = el.parentElement;
      }

      return null;
    };

    const measureOverflow = () => {
      const tableEl = tableRef.current;
      if (!tableEl) return;

      const scrollHost = findHorizontalScrollHost(tableEl);
      if (!scrollHost) {
        setIsHorizontallyOverflowing(false);
        return;
      }

      setIsHorizontallyOverflowing(scrollHost.scrollWidth > scrollHost.clientWidth + 1);
    };

    measureOverflow();

    const onResize = () => measureOverflow();
    window.addEventListener("resize", onResize);

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measureOverflow()) : null;
    if (observer && tableRef.current) {
      observer.observe(tableRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
    };
  }, [homeColsCheckboxes, filteredItems.length, loading, currentMonitorView]);

  const prevData = Object.values(trendStats?.PlayerStatistics["prev"] ?? {});
  const currentData = Object.values(trendStats?.PlayerStatistics["current"] ?? {});

  const playerTrends =
    trendStats &&
    prevData
      ?.map((prev) => {
        if (!currentData) return null;

        const player7 = currentData.find((p) => p.PlayerId === prev.PlayerId);

        return {
          PlayerId: prev.PlayerId,
          trends: getPlayerTrends(prev, currentData),
        };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);

  const playerTrendsMap = useMemo(() => {
    const map: Record<string, any> = {};

    trendStats?.PlayerStatistics["prev"].forEach((playerPrev) => {
      const playerCurrent = trendStats.PlayerStatistics["current"].find(
        (p) => p.PlayerId === playerPrev.PlayerId
      );

      if (playerCurrent && playerPrev.PlayerId) {
        map[playerPrev.PlayerId] = getPlayerTrends(playerPrev, playerCurrent);
      }
    });

    return map;
  }, [trendStats]);

  const [maxVMAX, setMaxVMAX] = useState<null | number>(null);
  const [minVMAX, setMinVMAX] = useState<null | number>(null);

  const [maxTotalDistance, setMaxTotalDisatance] = useState<null | number>(null);
  const [minTotalDistance, setMinTotalDisatance] = useState<null | number>(null);

  const [minRunDistanceTotal, setMinRunDistanceTotal] = useState<null | number>(null);

  const [maxRunDistanceTotal, setMaxRunDistanceTotal] = useState<null | number>(null);

  const [minHSR, setMinHSR] = useState<null | number>(null);
  const [maxHSR, setMaxHSR] = useState<null | number>(null);

  const [minHMLD, setMinHMLD] = useState<null | number>(null);
  const [maxHMLD, setMaxHMLD] = useState<null | number>(null);

  const [minSprint, setMinSprint] = useState<null | number>(null);
  const [maxSprint, setMaxSprint] = useState<null | number>(null);

  useEffect(() => {
    const maxValue = Math.max(
      ...playerProfiles.map((p) => parseFloat(String(p.MaxSpeedNLastDays).replace(",", ".")) || 0)
    );

    const minValue = Math.min(
      ...playerProfiles.map((p) => parseFloat(String(p.MaxSpeedNLastDays).replace(",", ".")) || 0)
    );

    if (minValue !== Infinity) {
      setMinVMAX(minValue * 0.9);
    }

    if (maxValue !== -Infinity) {
      setMaxVMAX(maxValue);
    }

    /////////// TOTAL DISTANCE ///////////

    const maxDistanceValue = Math.max(
      ...playerProfiles.map((p) => parseFloat(String(p.TotalDistance).replace(",", ".")) || 0)
    );

    const minDistanceValue = Math.min(
      ...playerProfiles.map((p) => parseFloat(String(p.TotalDistance).replace(",", ".")) || 0)
    );

    if (minDistanceValue !== Infinity) {
      setMinTotalDisatance(minDistanceValue * 0.9);
    }

    if (maxDistanceValue !== -Infinity) {
      setMaxTotalDisatance(maxDistanceValue);
    }

    /////////// GIEG ///////////

    const maxRunValue = Math.max(
      ...playerProfiles.map((p) => parseFloat(String(p.RunDistanceTotal).replace(",", ".")) || 0)
    );

    const minRunValue = Math.min(
      ...playerProfiles.map((p) => parseFloat(String(p.RunDistanceTotal).replace(",", ".")) || 0)
    );

    if (minRunValue !== Infinity) {
      setMinRunDistanceTotal(minRunValue * 0.9);
    }

    if (maxRunValue !== -Infinity) {
      setMaxRunDistanceTotal(maxRunValue);
    }

    /////////// HSR ///////////

    const maxHSRValue = Math.max(
      ...playerProfiles.map((p) => parseFloat(String(p.HSRDistanceTotal).replace(",", ".")) || 0)
    );

    const minHSRValue = Math.min(
      ...playerProfiles.map((p) => parseFloat(String(p.HSRDistanceTotal).replace(",", ".")) || 0)
    );

    if (minHSRValue !== Infinity) {
      setMinHSR(minHSRValue * 0.9);
    }

    if (maxHSRValue !== -Infinity) {
      setMaxHSR(maxHSRValue);
    }

    /////////// HMLD ///////////

    const maxHMLDValue = Math.max(
      ...playerProfiles.map((p) => parseFloat(String(p.HMLDDistance).replace(",", ".")) || 0)
    );

    const minHMLDValue = Math.min(
      ...playerProfiles.map((p) => parseFloat(String(p.HMLDDistance).replace(",", ".")) || 0)
    );

    if (minHMLDValue !== Infinity) {
      setMinHMLD(minHMLDValue * 0.9);
    }

    if (maxHMLDValue !== -Infinity) {
      setMaxHMLD(maxHMLDValue);
    }

    /////////// SPRINT ///////////

    const maxSprintValue = Math.max(
      ...playerProfiles.map((p) => parseFloat(String(p.SprintDistanceTotal).replace(",", ".")) || 0)
    );

    const minSprintValue = Math.min(
      ...playerProfiles.map((p) => parseFloat(String(p.SprintDistanceTotal).replace(",", ".")) || 0)
    );

    if (minSprintValue !== Infinity) {
      setMinSprint(minSprintValue * 0.9);
    }

    if (maxSprintValue !== -Infinity) {
      setMaxSprint(maxSprintValue);
    }
  }, [playerProfiles]);

  const dynamicColSpan = useMemo(() => {
    let total = 4;
    if (homeColsCheckboxes.klub.active) total += 1;
    if (homeColsCheckboxes.poz.active) total += 1;
    if (homeColsCheckboxes.lat.active) total += 1;
    if (homeColsCheckboxes.phv.active) total += 1;
    if (homeColsCheckboxes.dyspozycja.active) total += 1;
    if (homeColsCheckboxes.zadan.active) total += 1;
    if (homeColsCheckboxes.czas.active) total += 1;
    if (homeColsCheckboxes.dziennik.active) total += 1;
    if (homeColsCheckboxes.vmax.active) total += 1;
    if (homeColsCheckboxes.dystans.active) total += 1;
    if (homeColsCheckboxes.bieg.active) total += 1;
    if (homeColsCheckboxes.hsr.active) total += 1;
    if (homeColsCheckboxes.sprint.active) total += 1;
    if (homeColsCheckboxes.tts.active) total += 1;
    if (homeColsCheckboxes.hmld.active) total += 1;
    if (homeColsCheckboxes.accDec.active) total += 1;
    if (homeColsCheckboxes.moc.active) total += 1;
    if (homeColsCheckboxes.tetno.active) total += 1;
    if (homeColsCheckboxes.kalorie.active) total += 1;
    if (homeColsCheckboxes.playerLoad.active) total += 1;

    return total;
  }, [homeColsCheckboxes]);

  const howManyTdColsRemove = dynamicColSpan + 2 - (dynamicColSpan === 25 ? 1 : 3);

  return (
    <div>
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <table
          ref={tableRef}
          className={`table-styling ${isSortAnimating ? "sort-animating" : ""}`}
          style={{
            borderCollapse: "collapse",
            fontSize: "15px",
            tableLayout: "fixed",
            width: "max-content",
            borderSpacing: "px",
          }}
        >
          <thead
            style={{
              borderBottom: "7px solid var(--divider-color)",
            }}
          >
            <tr>
              <th
                rowSpan={3}
                style={{
                  ...stickyThStyle,
                  position: "sticky",
                  left: 0,
                  zIndex: 102,
                  background: "#2c363e",
                  color: "#f8f8f8",
                  borderRight: "1px solid #2c363e",
                  textAlign: "start",
                  paddingLeft: "11px",
                  width: "192px",
                  minWidth: "192px",
                  maxWidth: "192px",
                  backgroundColor: "rgb(27, 36, 44)",
                }}
              >
                {t("home.cols.player")}
              </th>
            </tr>

            {/* KLUB */}

            <tr>
              {homeColsCheckboxes.klub.active && (
                <th
                  rowSpan={3}
                  style={{
                    ...stickyThStyle,
                    position: "sticky",
                    // left: 0,
                    // zIndex: 102,
                    background: "rgb(28, 42, 53)",
                    color: "#f8f8f8",
                    borderRight: "1px solid #2c363e",
                    paddingLeft: "11px",
                    width: "192px",
                    minWidth: "192px",
                    maxWidth: "192px",
                    textAlign: "center",
                  }}
                >
                  {t("home.cols.klub")}
                </th>
              )}
            </tr>

            {/* POZ */}

            <tr>
              <OrderedCells
                keys={ORDERED_TABLE_COLUMN_KEYS}
                order={homeColumnOrder}
                sourceKeys={SOURCE_TABLE_COLUMN_KEYS}
                visibility={homeColsCheckboxes}
              >
                {homeColsCheckboxes.poz.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...mainTableHeaderBgColor,
                      width: "110px",
                      minWidth: "110px",
                      maxWidth: "110px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        width: "110px",
                        minWidth: "110px",
                        maxWidth: "110px",
                      }}
                    >
                      <div>{t("home.cols.poz")}</div>
                      <Arrow
                        onClick={() => {
                          toggleSort("poz");
                          filterBy("poz", "");
                        }}
                        style={getArrowStyle(sortField, "poz", sortDirection, arrowStyle)}
                      />
                    </div>
                  </th>
                )}

                {/* LAT */}
                {homeColsCheckboxes.lat.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...mainTableHeaderBgColor,
                      width: "73px",
                      minWidth: "73px",
                      maxWidth: "73px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        width: "73px",
                        minWidth: "73px",
                        maxWidth: "73px",
                      }}
                    >
                      <div>{t("home.cols.lat")}</div>
                      <Arrow
                        onClick={() => {
                          toggleSort("lat");
                          filterBy("Age", "");
                        }}
                        style={getArrowStyle(sortField, "lat", sortDirection, arrowStyle)}
                      />
                    </div>
                  </th>
                )}

                {/* PHV */}

                {homeColsCheckboxes.phv.active && (
                  <th
                    rowSpan={2}
                    style={{
                      ...stickyThStyle,
                      ...mainTableHeaderBgColor,
                      width: "73px",
                      minWidth: "73px",
                      maxWidth: "73px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        width: "73px",
                        minWidth: "73px",
                        maxWidth: "73px",
                      }}
                    >
                      <div>{t("home.cols.phv")}</div>
                      <Arrow
                        onClick={() => {
                          toggleSort("phv");
                          filterBy("PHV", "");
                        }}
                        style={getArrowStyle(sortField, "phv", sortDirection, arrowStyle)}
                      />
                    </div>
                  </th>
                )}

                {/* FORMA */}

                {/*   {homeColsCheckboxes.forma.active && (
                <th
                  colSpan={1}
                  style={{
                    ...stickyThStyle,
                    ...headerColOddBgColor,
                    width: "97px",
                    minWidth: "97px",
                    maxWidth: "97px",
                  }}
                >
                  <div
                    style={{
                      ...flexClass,
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        ...flexClass,
                      }}
                    >
                      FORMA
                    </span>

                    <InfoBubble>
                      <FormInfo />
                    </InfoBubble>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      height: "26px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        width: "100%",
                        ...headerSortOddUnitBgColor,
                      }}
                    >
                      <div>%</div>

                      <Arrow
                        onClick={() => {
                          toggleSort("forma");
                          filterBy("forma", "");
                        }}
                        style={getArrowStyle(
                          sortField,
                          "forma",
                          sortDirection,
                          arrowStyle,
                        )}
                      />
                    </div>
                  </div>
                </th>
              )} */}

                {/* DYSPOZYCJA */}

                {homeColsCheckboxes.dyspozycja.active && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "140px",
                      minWidth: "140px",
                      maxWidth: "140px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.dyspozycja")}
                      </span>
                      <InfoBubble>
                        <ReadinessInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>%</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("gotowość");
                            filterBy("gotowość", "");
                          }}
                          style={getArrowStyle(sortField, "gotowość", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* POTENCJAŁ */}
                {/*   {homeColsCheckboxes.potencjal.active && (
                <th
                  colSpan={1}
                  style={{
                    ...stickyThStyle,
                    ...headerColOddBgColor,
                    width: "140px",
                    minWidth: "140px",
                    maxWidth: "140px",
                  }}
                >
                  <div
                    style={{
                      ...flexClass,
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        ...flexClass,
                      }}
                    >
                      POTENCJAŁ
                    </span>

                    <InfoBubble>
                      <PotencialInfo />
                    </InfoBubble>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      height: "26px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        width: "100%",
                        ...headerSortOddUnitBgColor,
                      }}
                    >
                      <div>%</div>

                      <Arrow
                        onClick={() => {
                          toggleSort("potencjał");
                          filterBy("potencjał", "");
                        }}
                        style={getArrowStyle(
                          sortField,
                          "potencjał",
                          sortDirection,
                          arrowStyle,
                        )}
                      />
                    </div>
                  </div>
                </th>
              )} */}

                {/* ZADAŃ */}
                {homeColsCheckboxes.zadan.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "196px",
                      minWidth: "196px",
                      maxWidth: "196px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.zadan")}
                      </span>
                      <InfoBubble>
                        <TasksInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "50%",
                          lineHeight: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <span>liczba</span>
                        <Arrow
                          onClick={() => {
                            toggleSort("taskNumber");
                            filterBy("task", "number");
                          }}
                          style={getArrowStyle(sortField, "taskNumber", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "50%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <span>min.</span>
                        <Arrow
                          onClick={() => {
                            toggleSort("taskMin");
                            filterBy("task", "min");
                          }}
                          style={getArrowStyle(sortField, "taskMin", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {homeColsCheckboxes.czas.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "90px",
                      minWidth: "90px",
                      maxWidth: "90px",
                    }}
                  >
                    <div style={{ ...flexClass, height: "100%" }}>{t("home.cols.czas")}</div>
                  </th>
                )}

                {homeColsCheckboxes.dziennik.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "180px",
                      minWidth: "180px",
                      maxWidth: "180px",
                    }}
                  >
                    <div style={{ ...flexClass, height: "100%" }}>{t("home.cols.dziennik")}</div>
                  </th>
                )}

                {/* V MAX */}
                {homeColsCheckboxes.vmax.active && (
                  <th
                    rowSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "230px",
                      minWidth: "230px",
                      maxWidth: "230px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.vmax")}
                      </span>
                      <InfoBubble>
                        <VMAXInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>km/h</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("VMAX");
                            filterBy("VMAX", "");
                          }}
                          style={getArrowStyle(sortField, "VMAX", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* DYSTANS */}

                {homeColsCheckboxes.dystans.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "329px",
                      minWidth: "329px",
                      maxWidth: "329px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.dystans")}
                      </span>
                      <InfoBubble>
                        <DistanceInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                      className="column-divider"
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "226px",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <span>Total</span>
                        <Arrow
                          onClick={() => {
                            toggleSort("distanceTotal");
                            filterBy("distance", "total");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "distanceTotal",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "103px",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <span>m/min</span>
                        <Arrow
                          onClick={() => {
                            toggleSort("distanceAvg");
                            filterBy("distance", "Avg");
                          }}
                          style={getArrowStyle(sortField, "distanceAvg", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* BIEG */}

                {homeColsCheckboxes.bieg.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "329px",
                      minWidth: "329px",
                      maxWidth: "329px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.bieg")}
                      </span>
                      <InfoBubble>
                        <RunInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "226px",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>m</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("runM");
                            filterBy("run", "m");
                          }}
                          style={getArrowStyle(sortField, "runM", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100px",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>%</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("run%");
                            filterBy("run", "%");
                          }}
                          style={getArrowStyle(sortField, "run%", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* HSR */}

                {homeColsCheckboxes.hsr.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "329px",
                      minWidth: "329px",
                      maxWidth: "329px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.hsr")}
                      </span>
                      <InfoBubble>
                        <HSRInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "226px",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>m</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hsr");
                            filterBy("hsr", "m");
                          }}
                          style={getArrowStyle(sortField, "hsr", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100px",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>%</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hsrPercentage");
                            filterBy("hsr", "percentage");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "hsrPercentage",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* SPRINT */}

                {homeColsCheckboxes.sprint.active && (
                  <th
                    colSpan={3}
                    className="zadania-header"
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "428px",
                      minWidth: "428px",
                      maxWidth: "428px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.sprint")}
                      </span>
                      <InfoBubble>
                        <SprintInfo />
                      </InfoBubble>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "228px",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>m</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("sprint");
                            filterBy("sprint", "m");
                          }}
                          style={getArrowStyle(sortField, "sprint", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          // width: '33.33%',
                          width: "102px",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>%</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("sprint%");
                            filterBy("sprint", "%");
                          }}
                          style={getArrowStyle(sortField, "sprint%", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          // width: '33.33%',
                          width: "102px",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>licz.</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("sprintL");
                            filterBy("sprint", "L");
                          }}
                          style={getArrowStyle(sortField, "sprintL", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* TTS */}

                {homeColsCheckboxes.tts.active && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "97px",
                      minWidth: "97px",
                      maxWidth: "97px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.tts")}
                      </span>
                      <InfoBubble>
                        <TTSInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>sek.</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("tts");
                            filterBy("tts", "");
                          }}
                          style={getArrowStyle(sortField, "tts", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* HMLD */}

                {homeColsCheckboxes.hmld.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "329px",
                      minWidth: "329px",
                      maxWidth: "329px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.hmld")}
                      </span>
                      <InfoBubble>
                        <HMLDInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "226px",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>m</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hmldM");
                            filterBy("hmld", "m");
                          }}
                          style={getArrowStyle(sortField, "hmldM", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100px",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>%</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hmldPercentage");
                            filterBy("hmld", "percentage");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "hmldPercentage",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* ACC/DEC */}

                {homeColsCheckboxes.accDec.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "196px",
                      minWidth: "196px",
                      maxWidth: "196px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.accDec")}
                      </span>
                      <InfoBubble>
                        <PizInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "50%",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>ACC</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("pizAcceleration");
                            filterBy("piz", "acceleration");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "pizAcceleration",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "50%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>DEC</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("pizDeceleration");
                            filterBy("piz", "deceleration");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "pizDeceleration",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* MOC */}

                {homeColsCheckboxes.moc.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColEvenBgColor,
                      width: "300px",
                      minWidth: "300px",
                      maxWidth: "300px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.moc")}
                      </span>
                      <InfoBubble>
                        <PowerInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "50%",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>max W/kg</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("power");
                            filterBy("power", "max");
                          }}
                          style={getArrowStyle(sortField, "power", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "50%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortEvenUnitBgColor,
                        }}
                      >
                        <div>time &gt;14W/kg</div>
                        <Arrow style={arrowStyle} />
                      </div>
                    </div>
                  </th>
                )}

                {/* TĘTNO */}

                {homeColsCheckboxes.tetno.active && (
                  <th
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "490px",
                      minWidth: "490px",
                      maxWidth: "490px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.tetno")}
                      </span>
                      <InfoBubble>
                        <HeartRateInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>max</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("MaxHeartRate");
                            filterBy("Max", "HeartRate");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "MaxHeartRate",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>{t("home.avg")}</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("AverageHeartRate");
                            filterBy("Average", "HeartRate");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "AverageHeartRate",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>HR1</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hr1");
                            filterBy("hr1", "");
                          }}
                          style={getArrowStyle(sortField, "hr1", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>HR2</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hr2");
                            filterBy("hr2", "");
                          }}
                          style={getArrowStyle(sortField, "hr2", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>HR3</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hr3");
                            filterBy("hr3", "");
                          }}
                          style={getArrowStyle(sortField, "hr3", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>HR4</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hr4");
                            filterBy("hr4", "");
                          }}
                          style={getArrowStyle(sortField, "hr4", sortDirection, arrowStyle)}
                        />
                      </div>

                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          borderLeft: "2px solid var(--divider-color)",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>HR5</div>
                        <Arrow
                          onClick={() => {
                            toggleSort("hr5");
                            filterBy("hr5", "");
                          }}
                          style={getArrowStyle(sortField, "hr5", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* KALORIE */}

                {homeColsCheckboxes.kalorie.active && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "170px",
                      minWidth: "170px",
                      maxWidth: "170px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.kalorie")}
                      </span>
                      {/*   <InfoBubble> */}
                      {/*  <EnduraceInfo /> */}

                      {/*    </InfoBubble> */}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>Kcal</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("kalorie");
                            filterBy("kalorie", "");
                          }}
                          style={getArrowStyle(sortField, "kalorie", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* Player Load */}

                {homeColsCheckboxes.playerLoad.active && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "170px",
                      minWidth: "170px",
                      maxWidth: "170px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        {t("home.cols.playerLoad")}
                      </span>
                      {/*    <InfoBubble>

                      </InfoBubble> */}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div></div>

                        <Arrow
                          onClick={() => {
                            toggleSort("playerLoad");
                            filterBy("playerLoad", "");
                          }}
                          style={getArrowStyle(sortField, "playerLoad", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* DYNAMIKA */}

                {false && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "130px",
                      minWidth: "130px",
                      maxWidth: "130px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        DYNAMIKA
                      </span>
                      <InfoBubble>
                        <DynamicInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>%</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("dynamika");
                            filterBy("dynamika", "");
                          }}
                          style={getArrowStyle(sortField, "dynamika", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* INTENSYWNOŚĆ */}

                {false && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "170px",
                      minWidth: "170px",
                      maxWidth: "170px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        INTENSYWNOŚĆ
                      </span>
                      <InfoBubble>
                        <IntensityInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>%</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("intensity");
                            filterBy("intensity", "");
                          }}
                          style={getArrowStyle(sortField, "intensity", sortDirection, arrowStyle)}
                        />
                      </div>
                    </div>
                  </th>
                )}

                {/* WYTRZYMAŁOŚĆ */}

                {false && (
                  <th
                    colSpan={1}
                    style={{
                      ...stickyThStyle,
                      ...headerColOddBgColor,
                      width: "170px",
                      minWidth: "170px",
                      maxWidth: "170px",
                    }}
                  >
                    <div
                      style={{
                        ...flexClass,
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          ...flexClass,
                        }}
                      >
                        WYTRZYMAŁOŚĆ
                      </span>
                      <InfoBubble>
                        <EnduraceInfo />
                      </InfoBubble>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: "26px",
                      }}
                    >
                      <div
                        style={{
                          ...flexClass,
                          width: "100%",
                          ...headerSortOddUnitBgColor,
                        }}
                      >
                        <div>%</div>

                        <Arrow
                          onClick={() => {
                            toggleSort("wytrzymałość");
                            filterBy("wytrzymałość", "");
                          }}
                          style={getArrowStyle(
                            sortField,
                            "wytrzymałość",
                            sortDirection,
                            arrowStyle
                          )}
                        />
                      </div>
                    </div>
                  </th>
                )}
              </OrderedCells>
            </tr>
          </thead>

          <tbody>
            {/*  <tr
              style={{
                position: 'sticky',
                top: '47px',
                zIndex: 99,
                background: '#031524',
              }}
            >
              <td
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 101,
                  background: '#031524',
                  color: '#f8f8f8',
                  paddingLeft: '11px',
                  fontWeight: 400,
                  width: '192px',
                  minWidth: '192px',
                  maxWidth: '192px',
                }}
              >
                Grupa
              </td>
              <td
                colSpan={27}
                style={{
                  background: '#031524',
                  color: '#f8f8f8',
                }}
              />
            </tr> */}

            <tr
              style={{
                position: "sticky",
                top: "47px",
                zIndex: 99,
                //background: '#031524',
              }}
            >
              <td
                style={{
                  position: "sticky",
                  left: 0,
                  zIndex: 101,
                  background: "#031524",
                  color: "#f8f8f8",
                  paddingLeft: "11px",
                  fontWeight: 400,
                  width: "192px",
                  minWidth: "192px",
                  maxWidth: "192px",
                }}
              >
                {t("home.cols.group")}
              </td>
              <td
                colSpan={dynamicColSpan + 2 - 1}
                style={{
                  background: "#031524",
                  color: "#f8f8f8",
                }}
              />
            </tr>

            {/* KLUB */}

            <tr
              key={`average-statistics-${homeColumnOrder.join("-")}`}
              style={{
                position: "sticky",
                top: "73px",
                zIndex: 107,
                background: "#031524",
                fontWeight: "bold",
                height: "26px",
              }}
            >
              {homeColsCheckboxes.klub.active && (
                <td
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 107,
                    background: "#031524",
                    color: "#f8f8f8",
                    paddingLeft: "11px",
                    fontWeight: 400,
                    width: "192px",
                    minWidth: "192px",
                    maxWidth: "192px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {academyFolder && academyFolder?.label}
                  {/* {mainAcademyName?.length > 0 ? mainAcademyName : "-"} */}
                </td>
              )}

              <td
                style={{
                  // background: '#273e52',
                  borderRight: !homeColsCheckboxes.poz.active
                    ? "2px solid var(--divider-color)"
                    : "",
                }}
              ></td>

              {/* POZ */}

              <OrderedCells
                keys={ORDERED_TABLE_COLUMN_KEYS}
                order={homeColumnOrder}
                sourceKeys={SOURCE_TABLE_COLUMN_KEYS}
                visibility={homeColsCheckboxes}
                duplicateKeys={["sprint"]}
              >
                {homeColsCheckboxes.poz.active && <td style={academyStatsRows}></td>}

                {/* LAT */}

                {homeColsCheckboxes.lat.active && <td style={academyStatsRows}></td>}

                {/* PHV */}
                {homeColsCheckboxes.phv.active && <td style={academyStatsRows}></td>}

                {/* FORMA */}
                {/*   {homeColsCheckboxes.forma.active && (
                <td style={academyStatsRows}></td>
              )} */}

                {/* DYSPOZYCJA */}
                {homeColsCheckboxes.dyspozycja.active && <td style={academyStatsRows}></td>}

                {/* POTENCJAŁ */}
                {/*   {homeColsCheckboxes.potencjal.active && (
                <td
                  style={{
                    ...academyStatsRows,
                    borderRight: "2px solid #10293e",
                  }}
                ></td>
              )} */}

                {/* ZADAN */}
                {homeColsCheckboxes.zadan.active && (
                  <td
                    style={{
                      lineHeight: "24px",
                      whiteSpace: "nowrap",
                      ...headerSortOddUnitBgColor,
                      borderLeft: `2px solid var(--odd-background)`,
                    }}
                  >
                    <div
                      style={{
                        display: "inline-block",
                        verticalAlign: "top",
                        width: "100px",
                        minWidth: "100px",
                        maxWidth: "100px",
                        textAlign: "end",
                        paddingRight: "13px",
                        borderRight: "2px solid var(--divider-color)",
                        color: "transparent",
                      }}
                    >
                      {/* {avgStats?.EventCountNLastDays == null
                    ? '-'
                    : formattedValue(avgStats.EventCountNLastDays)}
 */}
                      .
                    </div>

                    <div
                      style={{
                        display: "inline-block",
                        verticalAlign: "top",
                        ...headerSortOddUnitBgColor,
                        width: "98px",
                        minWidth: "98px",
                        maxWidth: "98px",
                        textAlign: "end",
                        paddingRight: "13px",
                      }}
                    >
                      {avgStats?.EventTotalTime == null
                        ? "- "
                        : formattedValue(avgStats?.EventTotalTime)}
                    </div>
                  </td>
                )}

                {homeColsCheckboxes.czas.active && (
                  <td
                    style={{
                      ...academyStatsRows,
                      width: "90px",
                      minWidth: "90px",
                    }}
                  ></td>
                )}
                {homeColsCheckboxes.dziennik.active && (
                  <td
                    style={{
                      ...academyStatsRows,
                      width: "180px",
                      minWidth: "180px",
                    }}
                  ></td>
                )}
                {/* VMAX SREDNIA AKADEMII */}

                {homeColsCheckboxes.vmax.active && (
                  <td
                    style={{
                      ...headerSortEvenUnitBgColor,
                      width: "98px",
                      minWidth: "98px",
                      maxWidth: "98px",
                      textAlign: "end",
                      paddingRight: "67px",
                    }}
                  >
                    {avgStats?.MdnSpeedNLastDays == null
                      ? "-"
                      : formattedValue(avgStats?.MdnSpeedNLastDays)}
                  </td>
                )}

                {/* DYSTANS */}

                {homeColsCheckboxes.dystans.active && (
                  <td
                    style={{
                      lineHeight: "24px",
                      whiteSpace: "nowrap",
                      ...headerSortOddUnitBgColor,
                    }}
                  >
                    <div
                      style={{
                        display: "inline-block",
                        verticalAlign: "top",
                        width: "226px",
                        minWidth: "226px",
                        maxWidth: "226px",
                        textAlign: "end",
                        paddingRight: "62px",
                        borderRight: "2px solid var(--divider-color)",
                      }}
                    >
                      {avgStats?.MdnDistanceTotal == null
                        ? "-"
                        : formattedValue(avgStats?.MdnDistanceTotal)}
                    </div>
                    <div
                      style={{
                        display: "inline-block",
                        verticalAlign: "top",
                        width: "10px",
                        minWidth: "100px",
                        maxWidth: "100px",
                        textAlign: "end",
                        paddingRight: "9px",
                      }}
                    >
                      {avgStats?.MdnDistanceTotal == null
                        ? "-"
                        : formattedValue(
                            (avgStats.MdnDistanceTotal / (avgStats.EventTotalTime ?? 1)).toFixed()
                          )}
                    </div>
                  </td>
                )}

                {/* BIEG */}

                {homeColsCheckboxes.bieg.active && (
                  <td
                    style={{
                      ...headerSortEvenUnitBgColor,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                        ...headerSortEvenUnitBgColor,
                      }}
                    >
                      <div
                        style={{
                          width: "226px",
                          minWidth: "226px",
                          maxWidth: "226px",
                          textAlign: "end",
                          paddingRight: "64px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.MdnRunTotal == null
                          ? "-"
                          : formattedValue(avgStats?.MdnRunTotal)}
                      </div>
                      <div
                        style={{
                          width: "100px",
                          minWidth: "100px",
                          maxWidth: "100px",
                          textAlign: "end",
                          paddingRight: "13px",
                        }}
                      >
                        {avgStats?.MdnRunDistancePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.MdnRunDistancePercentage)}
                      </div>
                    </div>
                  </td>
                )}

                {/* HSR */}

                {homeColsCheckboxes.hsr.active && (
                  <td
                    style={{
                      ...headerSortOddUnitBgColor,
                      // borderRight: '4x solid #10293e',
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                        ...headerSortOddUnitBgColor,
                      }}
                    >
                      <div
                        style={{
                          width: "226px",
                          minWidth: "226px",
                          maxWidth: "226px",
                          textAlign: "end",
                          paddingRight: "64px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.MdnHSRTotal == null
                          ? "-"
                          : formattedValue(avgStats?.MdnHSRTotal)}
                      </div>
                      <div
                        style={{
                          width: "100px",
                          minWidth: "100px",
                          maxWidth: "100px",
                          textAlign: "end",
                          paddingRight: "13px",
                        }}
                      >
                        {avgStats?.MdnHSRDistancePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.MdnHSRDistancePercentage)}
                      </div>
                    </div>
                  </td>
                )}

                {/* SPRINT */}

                {homeColsCheckboxes.sprint.active && (
                  <td
                    style={{
                      ...headerSortOddUnitBgColor,
                      width: "392px",
                      minWidth: "392px",
                      maxWidth: "392px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                      }}
                    >
                      <span
                        style={{
                          width: "227px",
                          minWidth: "227px",
                          maxWidth: "227px",
                          textAlign: "end",
                          paddingRight: "62px",
                          borderRight: "2px solid var(--divider-color)",
                          height: "24px",
                        }}
                      >
                        {avgStats?.MdnSprintTotal == null
                          ? "-"
                          : formattedValue(avgStats?.MdnSprintTotal)}
                      </span>

                      <span
                        style={{
                          width: "102px",
                          minWidth: "102px",
                          maxWidth: "102px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.MdnSprintDistancePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.MdnSprintDistancePercentage)}
                      </span>

                      <span
                        style={{
                          width: "96px",
                          minWidth: "96px",
                          maxWidth: "96px",
                          textAlign: "end",
                          paddingRight: "7px",
                          background: "var(--even-background)",
                          borderTop: "1px solid var(--even-background)",
                        }}
                      >
                        {avgStats?.SprintNumberTotal == null
                          ? "-"
                          : formattedValue(avgStats?.SprintNumberTotal)}
                      </span>
                    </div>
                  </td>
                )}

                {homeColsCheckboxes.sprint.active && (
                  <>
                    <td
                      style={{
                        border: "1px solid var(--even-background)",
                      }}
                    ></td>
                    <td
                      style={{
                        borderTop: "solid 1px var(--even-background)",
                      }}
                    ></td>
                  </>
                )}

                {/* TTS */}

                {homeColsCheckboxes.tts.active && (
                  <td
                    style={{
                      ...headerSortOddUnitBgColor,
                      textAlign: "end",
                      paddingRight: "13px",
                    }}
                  >
                    {avgStats?.TimeToSprintInSeconds == null
                      ? "-"
                      : formattedValue(avgStats?.TimeToSprintInSeconds)}
                  </td>
                )}

                {/* HMLD */}

                {homeColsCheckboxes.hmld.active && (
                  <td
                    style={{
                      ...headerSortEvenUnitBgColor,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                      }}
                    >
                      <div
                        style={{
                          width: "226px",
                          minWidth: "226px",
                          maxWidth: "226px",
                          textAlign: "end",
                          paddingRight: "65px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.MdnHMLDTotal == null
                          ? "-"
                          : formattedValue(avgStats?.MdnHMLDTotal)}
                      </div>
                      <div
                        style={{
                          width: "100px",
                          textAlign: "end",
                          paddingRight: "11px",
                        }}
                      >
                        {avgStats?.MdnHMLDPercentage == null
                          ? "-"
                          : formattedValue(avgStats?.MdnHMLDPercentage)}
                      </div>
                    </div>
                  </td>
                )}

                {/* ACC/DEC */}

                {homeColsCheckboxes.accDec.active && (
                  <td
                    style={{
                      ...headerSortEvenUnitBgColor,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats.AccelerationNumberTotal == null
                          ? "-"
                          : formattedValue(avgStats.AccelerationNumberTotal)}
                      </div>
                      <div
                        style={{
                          width: "100%",
                          textAlign: "end",
                          paddingRight: "12px",
                        }}
                      >
                        {avgStats.DecelerationNumberTotal == null
                          ? "-"
                          : formattedValue(avgStats.DecelerationNumberTotal)}
                      </div>
                    </div>
                  </td>
                )}

                {/* MOC */}

                {homeColsCheckboxes.moc.active && (
                  <td
                    style={{
                      ...headerSortEvenUnitBgColor,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                      }}
                    >
                      <div
                        style={{
                          width: "151px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.MaxPowerOutput == null
                          ? "-"
                          : formattedValue(avgStats?.MaxPowerOutput)}
                      </div>
                      <div
                        style={{
                          width: "146px",
                          textAlign: "end",
                          paddingRight: "20px",
                        }}
                      >
                        {avgStats?.PowerTime == null ? "-" : formattedValue(avgStats?.PowerTime)}
                      </div>
                    </div>
                  </td>
                )}

                {/* TETNO */}
                {homeColsCheckboxes.tetno.active && (
                  <td
                    style={{
                      ...headerSortOddUnitBgColor,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        height: "24px",
                        width: "490px",
                        minWidth: "490px",
                      }}
                    >
                      <div
                        style={{
                          width: "71px",
                          minWidth: "71px",
                          textAlign: "end",
                          paddingRight: "11px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.MaxHeartRate == null
                          ? "-"
                          : formattedValue(avgStats?.MaxHeartRate)}
                      </div>
                      <div
                        style={{
                          width: "70px",
                          minWidth: "70px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.HeartRateLastNDaysAvg == null
                          ? "-"
                          : formattedValue(avgStats?.HeartRateLastNDaysAvg)}
                      </div>

                      <div
                        style={{
                          width: "70px",
                          minWidth: "70px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.HeartRate1ZonePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.HeartRate1ZonePercentage)}
                      </div>

                      <div
                        style={{
                          width: "70px",
                          minWidth: "70px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.HeartRate2ZonePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.HeartRate2ZonePercentage)}
                      </div>

                      <div
                        style={{
                          width: "70px",
                          minWidth: "70px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.HeartRate3ZonePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.HeartRate3ZonePercentage)}
                      </div>

                      <div
                        style={{
                          width: "70px",
                          minWidth: "70px",
                          textAlign: "end",
                          paddingRight: "13px",
                          borderRight: "2px solid var(--divider-color)",
                        }}
                      >
                        {avgStats?.HeartRate4ZonePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.HeartRate4ZonePercentage)}
                      </div>

                      <div
                        style={{
                          width: "70px",
                          minWidth: "70px",
                          textAlign: "end",
                          paddingRight: "13px",
                        }}
                      >
                        {avgStats?.HeartRate5ZonePercentage == null
                          ? "-"
                          : formattedValue(avgStats?.HeartRate5ZonePercentage)}
                      </div>
                    </div>
                  </td>
                )}

                {/* KALORIE */}

                {homeColsCheckboxes.kalorie.active && (
                  <td
                    style={{
                      ...headerColOddBgColor,
                      textAlign: "end",
                      color: "rgba(248, 248, 248, 0.698)",
                      paddingRight: "13px",
                      // borderRight: '4px solid #10293e',
                    }}
                  >
                    {avgStats?.BurnCalories == null ? "-" : formattedValue(avgStats?.BurnCalories)}
                  </td>
                )}

                {/* PLAYER LOAD */}

                {homeColsCheckboxes.playerLoad.active && (
                  <td
                    style={{
                      ...headerColOddBgColor,
                      textAlign: "end",
                      color: "rgba(248, 248, 248, 0.698)",
                      paddingRight: "13px",
                      // borderRight: '4px solid #10293e',
                    }}
                  >
                    {avgStats?.Load == null ? "-" : formattedValue(avgStats?.Load)}
                  </td>
                )}

                {/* DYNAMIKA */}

                {false && (
                  <td
                    style={{
                      ...headerColOddBgColor,
                      textAlign: "end",
                      color: "rgba(248, 248, 248, 0.698)",
                      paddingRight: "13px",
                      // borderRight: '4px solid #10293e',
                    }}
                  >
                    {avgStats?.DynamicPercentage == null
                      ? "-"
                      : formattedValue(avgStats.DynamicPercentage!)}
                  </td>
                )}

                {/* INTENSYWNOŚĆ */}

                {false && (
                  <td
                    style={{
                      ...headerColOddBgColor,
                      textAlign: "end",
                      color: "rgba(248, 248, 248, 0.698)",
                      paddingRight: "13px",
                      // borderRight: '4px solid #10293e',
                    }}
                  >
                    {avgStats?.IntensityPercentage == null
                      ? "-"
                      : formattedValue(avgStats.IntensityPercentage!)}
                  </td>
                )}

                {/* WYTRZYMAŁOŚĆ */}

                {false && (
                  <td
                    style={{
                      ...headerColOddBgColor,
                      textAlign: "end",
                      color: "rgba(248, 248, 248, 0.698)",
                      paddingRight: "13px",
                      // borderRight: '4px solid #10293e',
                    }}
                  >
                    {avgStats?.EndurancePercentage == null
                      ? "-"
                      : formattedValue(avgStats.EndurancePercentage!)}
                  </td>
                )}
              </OrderedCells>
            </tr>
            {loading || filteredItems.length === 0 ? (
              <td colSpan={dynamicColSpan + 2 - 1}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: isHorizontallyOverflowing ? "flex-start" : "center",
                    paddingLeft: isHorizontallyOverflowing
                      ? `${scrollX + currentMonitorView / 2 - 100}px`
                      : undefined,
                  }}
                  className=" bg-opacity-5 bg-white"
                >
                  {loading && (
                    <LoaderRing
                      cssStyle={{
                        justifyContent: isHorizontallyOverflowing ? "start" : "center",
                        background: "transparent",
                      }}
                      text="wczytywanie danych..."
                    />
                  )}
                </div>
                {!playerProfiles.length && !loading && (
                  <div
                    className="flex  items-center text-center text-white opacity-70 h-12 font-medium bg-white bg-opacity-5 uppercase"
                    style={{
                      justifyContent: isHorizontallyOverflowing ? "flex-start" : "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignContent: "center",
                        paddingLeft: isHorizontallyOverflowing
                          ? `${scrollX + currentMonitorView / 2 - 100}px`
                          : undefined,
                      }}
                    >
                      Brak danych
                    </div>
                  </div>
                )}
              </td>
            ) : (
              <></>
            )}

            {!loading &&
              filteredItems.length > 0 &&
              filteredItems.map((data: any, index: number) => {
                const trends = playerTrendsMap[data.PlayerId];

                return (
                  <>
                    <tr
                      style={{
                        borderTop: "3px solid var(--divider-color)",
                      }}
                    >
                      <td
                        style={{
                          background: "rgb(28, 42, 53)",
                          color: "#f8f8f8b2",
                          width: "150px",
                          height: "26px",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          paddingLeft: "11px",
                        }}
                      >
                        {data?.FirstName ?? "-"}
                      </td>
                      {/*   {Array.from({ length: howManyTdColsRemove }).map(
                        (_, i) => (
                          <td
                            key={i}
                            style={{
                              background: '#02020f',
                            }}
                          ></td>
                        )
                      )} */}
                    </tr>

                    <tr
                      style={{
                        borderBottom: "var(--divider-width) solid var(--divider-color)",
                      }}
                    >
                      <td
                        style={{
                          background: "rgb(28, 42, 53)",
                          color: "#f8f8f8b2",
                          paddingLeft: "11px",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                        }}
                      >
                        {data?.LastName ?? "-"}
                      </td>

                      {/* KLUB */}

                      {homeColsCheckboxes.klub.active && (
                        <tr
                          style={{
                            height: "30px",
                          }}
                        >
                          <td
                            colSpan={1}
                            style={{
                              width: "100%",
                              minWidth: "192px",
                              color: "#f8f8f8b2",
                              textAlign: "center",
                              borderRight: "2px solid #142c40",

                              borderLeft: "2px solid #142c40",

                              background: "rgb(28, 42, 53)",
                            }}
                          >
                            {data?.Academy.Name ?? "-"}
                          </td>
                        </tr>
                      )}

                      {/* POZ */}

                      <OrderedCells
                        keys={ORDERED_TABLE_COLUMN_KEYS}
                        order={homeColumnOrder}
                        sourceKeys={SOURCE_TABLE_COLUMN_KEYS}
                        visibility={homeColsCheckboxes}
                      >
                        {homeColsCheckboxes.poz.active && (
                          <td
                            style={{
                              width: "100%",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            <div
                              style={{
                                height: "26px",
                                //background: '#273e52',
                                width: "100%",
                                paddingLeft: "8px",
                              }}
                            >
                              {PreferredPosition[
                                data?.PreferredPosition as keyof typeof PreferredPosition
                              ] || "-"}
                            </div>
                          </td>
                        )}

                        {/* LAT */}

                        {homeColsCheckboxes.lat.active && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "73px",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            {data?.Age != null ? (
                              <div
                                style={{
                                  padding: "0 10px 0 0",
                                  width: "100%",
                                  //background: '#273e52',
                                  height: "26px",
                                  ...flexEndClass,
                                }}
                              >
                                {data?.Age}
                              </div>
                            ) : (
                              <div
                                style={{
                                  padding: "0 10px 0 0",
                                  width: "100%",
                                  //background: '#273e52',
                                  height: "26px",
                                  ...flexEndClass,
                                }}
                              >
                                -
                              </div>
                            )}
                          </td>
                        )}

                        {/* PHV */}

                        {homeColsCheckboxes.phv.active && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "73px",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            <div
                              style={{
                                padding: "0 10px 0 0",
                                width: "100%",
                                //background: '#273e52',
                                height: "26px",
                                ...flexEndClass,
                              }}
                            >
                              {data?.PHV != null ? (
                                formattedValue(data?.PHV)
                              ) : (
                                <div
                                  style={{
                                    padding: "0 10px 0 0",
                                    width: "100%",
                                    //background: '#273e52',
                                    height: "26px",
                                    ...flexEndClass,
                                  }}
                                >
                                  -
                                </div>
                              )}
                            </div>
                          </td>
                        )}

                        {/* FORMA */}
                        {/*    {homeColsCheckboxes.forma.active && (
                        <td
                          style={{
                            textAlign: "end",
                            width: "93px",
                            color: "rgba(248, 248, 248, 0.698)",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",

                              height: "26px",
                              display: "flex",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",

                                height: "26px",
                                ...flexEndClass,
                              }}
                            >
                              {data?.Form !== null &&
                              avgStats?.Form !== null ? (
                                <div
                                  style={{
                                    padding: "0 10px 0 0",
                                  }}
                                >
                                  {" "}
                                  {data?.Form}{" "}
                                </div>
                              ) : (
                                <span
                                  style={{
                                    padding: "0 13px 0 0",
                                  }}
                                >
                                  -
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      )} */}

                        {/* DYSPOZYCJA */}

                        {homeColsCheckboxes.dyspozycja.active && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "103px",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                //background: '#273e52',
                                height: "26px",
                                ...flexEndClass,
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  //background: '#273e52',
                                  height: "26px",
                                  ...flexEndClass,
                                }}
                              >
                                {data?.Readiness !== null && avgStats?.Readiness !== null ? (
                                  <div
                                    style={{
                                      padding: "0 10px 0 0",
                                    }}
                                  >
                                    {" "}
                                    {data?.Readiness}{" "}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 10px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* POTENCJAŁ */}

                        {/*    {homeColsCheckboxes.potencjal.active && (
                        <td
                          style={{
                            textAlign: "end",
                            width: "103px",
                            color: "rgba(248, 248, 248, 0.698)",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",

                              height: "26px",
                              ...flexEndClass,
                            }}
                          >
                            <div
                              style={{
                                width: "100%",

                                height: "26px",
                                ...flexEndClass,
                              }}
                            >
                              {data?.Potential !== null &&
                              avgStats?.Potential !== null ? (
                                <div
                                  style={{
                                    padding: "0 13px 0 0",
                                  }}
                                >
                                  {" "}
                                  {data?.Potential}{" "}
                                </div>
                              ) : (
                                <span
                                  style={{
                                    padding: "0 13px 0 0",
                                  }}
                                >
                                  -
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      )}
 */}
                        {/* ZADAŃ */}
                        {homeColsCheckboxes.zadan.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                              width: "196px",
                              minWidth: "196px",
                              maxWidth: "196px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "100%",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a3f50',
                                }}
                                className="column-divider"
                              >
                                {data?.EventCountNLastDays !== null &&
                                avgStats?.EventCountNLastDays !== null ? (
                                  <div
                                    style={{
                                      padding: "1px 13px 0 0",
                                    }}
                                  >
                                    {data.EventCountNLastDays}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      background: "rgb(240, 244, 247)",
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "100%",
                                  textAlign: "end",
                                }}
                              >
                                {data?.EventTotalTimeInMinutes !== null &&
                                avgStats?.EventTotalTimeInMinutes !== null ? (
                                  getStyledSpan(
                                    data.EventTotalTimeInMinutes ?? 0,
                                    avgStats.EventTotalTime ?? 0
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {homeColsCheckboxes.czas.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                              width: "90px",
                              minWidth: "90px",
                              maxWidth: "90px",
                              textAlign: "left",
                              paddingLeft: "8px",
                            }}
                          >
                            {formatTimelineTimeOnField(data?.TimelineSummary)}
                          </td>
                        )}

                        {homeColsCheckboxes.dziennik.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                              width: "180px",
                              minWidth: "180px",
                              maxWidth: "180px",
                              textAlign: "left",
                            }}
                          >
                            <PlayerTimelineDiary summary={data?.TimelineSummary} />
                          </td>
                        )}
                        {/* V MAX */}
                        {homeColsCheckboxes.vmax.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                              width: "200px",
                              minWidth: "200px",
                              maxWidth: "200px",
                              textAlign: "end",
                              //background: 'transparent',
                            }}
                          >
                            {data?.MaxSpeedNLastDays !== null &&
                            avgStats?.MaxSpeedNLastDays !== null ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  {getStyledSpanWithProgressbar(
                                    data.MaxSpeedNLastDays,
                                    maxVMAX ?? 0,
                                    minVMAX ?? 0,
                                    avgStats.MdnSpeedNLastDays
                                  )}
                                </div>

                                <div
                                  style={{
                                    width: "120px",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  {isTrendChecked ? (
                                    renderTrendSpan(
                                      data.MaxSpeedNLastDays,
                                      trends?.MaxSpeedNLastDays
                                    )
                                  ) : (
                                    <span
                                      style={{
                                        width: "120px",
                                        minWidth: "120px",
                                        maxWidth: "120px",
                                        textAlign: "right",
                                        //background: '#2a4054',
                                        height: "26px",
                                        paddingRight: "13px",
                                      }}
                                    >
                                      {data.MaxSpeedNLastDays}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span
                                style={{
                                  width: "120px",
                                  minWidth: "120px",
                                  maxWidth: "120px",
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  //background: '#2a4054',
                                  height: "26px",
                                  paddingRight: "13px",
                                }}
                              >
                                –
                              </span>
                            )}
                          </td>
                        )}

                        {/* DYSTANS */}

                        {homeColsCheckboxes.dystans.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "228px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  // background: '#2a4054',
                                }}
                              >
                                {
                                  <div
                                    style={{
                                      display: "flex",
                                      alignContent: "center",
                                      width: "100%",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                      }}
                                    >
                                      {getStyledSpanWithProgressbar(
                                        data.TotalDistance,
                                        maxTotalDistance ?? 0,
                                        minTotalDistance ?? 0,
                                        avgStats.MdnDistanceTotal
                                      )}
                                    </div>

                                    {isTrendChecked ? (
                                      <div
                                        style={{
                                          width: "120px",
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        {renderTrendSpan(data.TotalDistance, trends?.TotalDistance)}
                                      </div>
                                    ) : (
                                      <span
                                        style={{
                                          padding: "0 13px 0 0",
                                          width: "120px",
                                          minWidth: "120px",
                                          maxWidth: "120px",
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          //background: '#2a4054',
                                        }}
                                      >
                                        {data.TotalDistance}
                                      </span>
                                    )}
                                  </div>
                                }
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  textAlign: "end",
                                  width: "103px",
                                }}
                                className="column-divider"
                              >
                                {data?.AverageDistance !== null &&
                                avgStats?.AverageDistance !== null ? (
                                  getStyledSpan(
                                    data.TotalDistance / data.EventTotalTimeInMinutes ?? 0,
                                    avgStats.TotalDistance ?? 0
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      background: "#2a4054",
                                      display: "inline-block",
                                      width: "102px",
                                      height: "26px",
                                    }}
                                  >
                                    {data.TotalDistance / data.EventTotalTimeInMinutes}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* BIEG */}

                        {homeColsCheckboxes.bieg.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "226px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: 'transparent',
                                }}
                              >
                                {data?.RunDistanceTotal !== null &&
                                avgStats?.MdnRunTotal !== null ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignContent: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                      }}
                                    >
                                      {" "}
                                      {getStyledSpanWithProgressbar(
                                        data.RunDistanceTotal,
                                        maxRunDistanceTotal ?? 0,
                                        minRunDistanceTotal ?? 0,
                                        avgStats.MdnRunTotal
                                      )}
                                    </div>

                                    {isTrendChecked ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          width: "120px",
                                          minWidth: "120px",
                                          maxWidth: "120px",
                                        }}
                                      >
                                        {renderTrendSpan(
                                          data.RunDistanceTotal,
                                          trends?.RunDistanceTotal
                                        )}
                                      </div>
                                    ) : (
                                      <span
                                        style={{
                                          padding: "0 13px 0 0",
                                          width: "120px",
                                          minWidth: "120px",
                                          maxWidth: "120px",
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          // background: '#2a4054',
                                        }}
                                      >
                                        {data?.RunDistanceTotal}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      width: "120px",
                                      minWidth: "120px",
                                      maxWidth: "120px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "101px",
                                  textAlign: "end",
                                }}
                              >
                                {data?.RunDistancePercentage !== null &&
                                avgStats?.RunDistancePercentage !== null ? (
                                  getStyledSpan(
                                    data.RunDistancePercentage,
                                    avgStats.RunDistancePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      width: "101px",
                                      minWidth: "101px",
                                      maxWidth: "101px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* HSR */}

                        {homeColsCheckboxes.hsr.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "226px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  background: "transparent",
                                }}
                              >
                                {data?.HSRDistanceTotal !== null &&
                                avgStats?.MdnHSRTotal !== null ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignContent: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                      }}
                                    >
                                      {" "}
                                      {getStyledSpanWithProgressbar(
                                        data.HSRDistanceTotal,
                                        maxHSR ?? 0,
                                        minHSR ?? 0,
                                        avgStats.MdnHSRTotal
                                      )}
                                    </div>

                                    {isTrendChecked ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          width: "120px",
                                        }}
                                      >
                                        {renderTrendSpan(
                                          data.HSRDistanceTotal,
                                          trends?.HSRDistanceTotal
                                        )}
                                      </div>
                                    ) : (
                                      <span
                                        style={{
                                          padding: "0 13px 0 0",
                                          width: "100px",
                                          minWidth: "100px",
                                          maxWidth: "100px",
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        {data?.HSRDistanceTotal}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      width: "120px",
                                      minWidth: "120px",
                                      maxWidth: "120px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "101px",
                                  textAlign: "end",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.HSRDistancePercentage !== null &&
                                avgStats?.HSRDistancePercentage !== null ? (
                                  getStyledSpan(
                                    data.HSRDistancePercentage,
                                    avgStats.HSRDistancePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      width: "101px",
                                      minWidth: "101px",
                                      maxWidth: "101px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* SPRINT */}

                        {homeColsCheckboxes.sprint.active && (
                          <td
                            colSpan={3}
                            style={{
                              color: "#f8f8f8b2",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                height: "23px",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "227px",
                                  minWidth: "227px",
                                  maxWidth: "227px",
                                  textAlign: "end",
                                }}
                              >
                                {data?.SprintDistanceTotal !== null &&
                                avgStats?.MdnSprintTotal !== null ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignContent: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                      }}
                                    >
                                      {" "}
                                      {getStyledSpanWithProgressbar(
                                        data.SprintDistanceTotal,
                                        maxSprint ?? 0,
                                        minSprint ?? 0,
                                        avgStats.MdnSprintTotal
                                      )}
                                    </div>

                                    {isTrendChecked ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          width: "120px",
                                        }}
                                      >
                                        {renderTrendSpan(
                                          data.SprintDistanceTotal,
                                          trends?.SprintDistanceTotal
                                        )}
                                      </div>
                                    ) : (
                                      <span
                                        style={{
                                          padding: "0 13px 0 0",
                                          width: "120px",
                                          minWidth: "120px",
                                          maxWidth: "120px",
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          borderRight: "2px solid rgba(0,0,0,0.5)",
                                        }}
                                      >
                                        {data?.SprintDistanceTotal}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      width: "120px",
                                      minWidth: "120px",
                                      maxWidth: "120px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "101px",
                                  textAlign: "end",
                                  borderRight: "2px solid rgba(0,0,0,0.5)",
                                  height: "26px",
                                }}
                              >
                                {data?.SprintDistancePercentage !== null &&
                                avgStats?.SprintDistancePercentage !== null ? (
                                  getStyledSpan(
                                    data.SprintDistancePercentage,
                                    avgStats.SprintDistancePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      height: "27px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "103px",
                                  textAlign: "end",
                                  height: "26px",
                                  borderLeft: "2px solid #142c40",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.SprintNumberTotal !== null &&
                                avgStats?.SprintNumberTotal !== null ? (
                                  getStyledSpan(data.SprintNumberTotal, avgStats.SprintNumberTotal)
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* TTS */}

                        {homeColsCheckboxes.tts.active && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "93px",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: "26px",
                              }}
                            >
                              {data?.TimeToSprintInSeconds !== null &&
                              avgStats?.TimeToSprintInSeconds !== null &&
                              data.TimeToSprintInSeconds !== "0,00" ? (
                                getStyledSpan(
                                  data.TimeToSprintInSeconds,
                                  avgStats.TimeToSprintInSeconds
                                )
                              ) : (
                                <span
                                  style={{
                                    padding: "0 13px 0 0",
                                  }}
                                >
                                  {data.TimeToSprintInSeconds === "0,00" ? "-" : "-"}
                                </span>
                              )}
                            </div>
                          </td>
                        )}

                        {/* HMLD */}
                        {homeColsCheckboxes.hmld.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "226px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                }}
                              >
                                {data?.HMLDDistance !== null && avgStats?.MdnHMLDTotal !== null ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignContent: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                      }}
                                    >
                                      {" "}
                                      {getStyledSpanWithProgressbar(
                                        data.HMLDDistance,
                                        maxHMLD ?? 0,
                                        minHMLD ?? 0,
                                        avgStats.MdnHMLDTotal
                                      )}
                                    </div>

                                    {isTrendChecked ? (
                                      <div
                                        style={{
                                          width: "120px",
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        {renderTrendSpan(data?.HMLDDistance, trends?.HMLDDistance)}
                                      </div>
                                    ) : (
                                      <span
                                        style={{
                                          padding: "0 13px 0 0",
                                          display: "inline-block",
                                          width: "100px",
                                          minWidth: "100px",
                                          maxWidth: "100px",
                                          height: "26px",
                                        }}
                                      >
                                        {data?.HMLDDistance}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      display: "inline-block",
                                      width: "100px",
                                      minWidth: "100px",
                                      maxWidth: "100px",
                                      height: "26px",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "102px",
                                  textAlign: "end",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.HMLDDistancePercentage !== null &&
                                avgStats?.HMLDDistancePercentage !== null ? (
                                  getStyledSpan(
                                    data.HMLDDistancePercentage,
                                    avgStats.HMLDDistancePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* ACC/DEC */}

                        {homeColsCheckboxes.accDec.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "100%",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.AccelerationNumberTotal !== null &&
                                avgStats?.AccelerationNumberTotal !== null ? (
                                  getStyledSpan(
                                    data.AccelerationNumberTotal,
                                    avgStats.AccelerationNumberTotal
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "100%",
                                  textAlign: "end",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.DecelerationNumberTotal !== null &&
                                avgStats?.DecelerationNumberTotal !== null ? (
                                  getStyledSpan(
                                    data.DecelerationNumberTotal,
                                    avgStats.DecelerationNumberTotal
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* MOC */}

                        {homeColsCheckboxes.moc.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "151px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                }}
                              >
                                {data?.MaxPowerOutput !== null &&
                                avgStats?.MaxPowerOutput !== null ? (
                                  getStyledSpan(data.MaxPowerOutput, avgStats.MaxPowerOutput)
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "148px",
                                  textAlign: "end",
                                  padding: "0 10px 0 0",

                                  height: "26px",
                                }}
                              >
                                {data?.PowerTime !== null && avgStats?.PowerTime !== null ? (
                                  getStyledSpan(data.PowerTime, avgStats.PowerTime)
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* TĘTNO */}

                        {homeColsCheckboxes.tetno.active && (
                          <td
                            style={{
                              color: "#f8f8f8b2",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                width: "492px",
                                minWidth: "492px",
                              }}
                            >
                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "72px",
                                  minWidth: "72px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {
                                  // isTrendChecked ? (
                                  //   playerTrends &&
                                  //   data?.MaxHeartRate !== null &&
                                  //   avgStats?.MaxHeartRate !== null ? (
                                  //     <div>
                                  //       {renderTrendSpan(
                                  //         data.MaxHeartRate,
                                  //         trends?.MaxHeartRate ?? undefined
                                  //       )}
                                  //     </div>
                                  //   ) : (
                                  //     <span
                                  //       style={{
                                  //         padding: '0 13px 0 0',
                                  //       }}
                                  //     >
                                  //       -
                                  //     </span>
                                  //   )
                                  // ) :

                                  data?.MaxHeartRate !== null && avgStats?.MaxHeartRate !== null ? (
                                    getStyledSpan(data.MaxHeartRate, avgStats.MaxHeartRate)
                                  ) : (
                                    <span
                                      style={{
                                        padding: "0 13px 0 0",
                                      }}
                                    >
                                      -
                                    </span>
                                  )
                                }
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "70px",
                                  minWidth: "70px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {
                                  // isTrendChecked ? (
                                  //   playerTrends &&
                                  //   data?.HeartRateLastNDaysAvg !== null &&
                                  //   avgStats?.HeartRateLastNDaysAvg !== null ? (
                                  //     <div>
                                  //       {renderTrendSpan(
                                  //         data.HeartRateLastNDaysAvg,
                                  //         trends?.HeartRateLastNDaysAvg ?? undefined
                                  //       )}
                                  //     </div>
                                  //   ) : (
                                  //     <span
                                  //       style={{
                                  //         padding: '0 13px 0 0',
                                  //       }}
                                  //     >
                                  //       -
                                  //     </span>
                                  //   )
                                  // ) :

                                  data?.HeartRateLastNDaysAvg !== null &&
                                  avgStats?.HeartRateLastNDaysAvg !== null ? (
                                    getStyledSpan(
                                      data.HeartRateLastNDaysAvg,
                                      avgStats.HeartRateLastNDaysAvg
                                    )
                                  ) : (
                                    <span
                                      style={{
                                        padding: "0 13px 0 0",
                                      }}
                                    >
                                      -
                                    </span>
                                  )
                                }
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "70px",
                                  minWidth: "70px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {
                                  // isTrendChecked ? (
                                  //   playerTrends &&
                                  //   data?.HeartRate1ZonePercentage !== null &&
                                  //   avgStats?.HeartRate1ZonePercentage !== null ? (
                                  //     <div>
                                  //       {renderTrendSpan(
                                  //         data.HeartRate1ZonePercentage,
                                  //         trends?.HeartRate1ZonePercentage ??
                                  //           undefined
                                  //       )}
                                  //     </div>
                                  //   ) : (
                                  //     <span
                                  //       style={{
                                  //         padding: '0 13px 0 0',
                                  //       }}
                                  //     >
                                  //       -
                                  //     </span>
                                  //   )
                                  // ) :

                                  data?.HeartRate1ZonePercentage !== null &&
                                  avgStats?.HeartRate1ZonePercentage !== null ? (
                                    getStyledSpan(
                                      data.HeartRate1ZonePercentage,
                                      avgStats.HeartRate1ZonePercentage
                                    )
                                  ) : (
                                    <span
                                      style={{
                                        padding: "0 13px 0 0",
                                      }}
                                    >
                                      -
                                    </span>
                                  )
                                }
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "70px",
                                  minWidth: "70px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.HeartRate2ZonePercentage !== null &&
                                avgStats?.HeartRate2ZonePercentage !== null ? (
                                  getStyledSpan(
                                    data.HeartRate2ZonePercentage,
                                    avgStats.HeartRate2ZonePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "70px",
                                  minWidth: "70px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.HeartRate3ZonePercentage !== null &&
                                avgStats?.HeartRate3ZonePercentage !== null ? (
                                  getStyledSpan(
                                    data.HeartRate3ZonePercentage,
                                    avgStats.HeartRate3ZonePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "70px",
                                  minWidth: "70px",
                                  textAlign: "end",
                                  borderRight: "2px solid var(--divider-color)",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.HeartRate4ZonePercentage !== null &&
                                avgStats?.HeartRate4ZonePercentage !== null ? (
                                  getStyledSpan(
                                    data.HeartRate4ZonePercentage,
                                    avgStats.HeartRate4ZonePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  color: "#f8f8f8b2",
                                  width: "100%",
                                  textAlign: "end",
                                  //background: '#2a4054',
                                }}
                              >
                                {data?.HeartRate5ZonePercentage !== null &&
                                avgStats?.HeartRate5ZonePercentage !== null ? (
                                  getStyledSpan(
                                    data.HeartRate5ZonePercentage,
                                    avgStats.HeartRate5ZonePercentage
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* KALORIE */}

                        {homeColsCheckboxes.kalorie.active && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "120px",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            {data?.BurnCalories !== null && avgStats?.BurnCalories !== null ? (
                              getStyledSpan(data.BurnCalories, avgStats.BurnCalories)
                            ) : (
                              <span
                                style={{
                                  padding: "0 13px 0 0",
                                  height: "26px",
                                  background: "#2a4054",
                                  width: "130px",
                                  display: "inline-block",
                                }}
                              >
                                -
                              </span>
                            )}
                          </td>
                        )}

                        {/* Player Load */}

                        {homeColsCheckboxes.playerLoad.active && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "120px",
                              color: "rgba(248, 248, 248, 0.698)",
                              // background:'#2a4054',
                            }}
                          >
                            {data?.Load !== null && avgStats?.Load !== null ? (
                              getStyledSpan(data.Load, avgStats.Load)
                            ) : (
                              <span
                                style={{
                                  padding: "0 13px 0 0",
                                  height: "26px",
                                  background: "#2a4054",
                                  width: "130px",
                                  display: "inline-block",
                                }}
                              >
                                -
                              </span>
                            )}
                          </td>
                        )}

                        {/*  DYNAMIKA*/}

                        {false && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "120px",
                              color: "rgba(248, 248, 248, 0.698)",
                              // background:'#2a4054',
                            }}
                          >
                            {data?.DynamicPercentage !== null &&
                            avgStats?.DynamicPercentage !== null ? (
                              getStyledSpan(data.DynamicPercentage!, avgStats.DynamicPercentage!)
                            ) : (
                              <span
                                style={{
                                  padding: "0 13px 0 0",
                                  height: "26px",
                                  background: "#2a4054",
                                  width: "130px",
                                  display: "inline-block",
                                }}
                              >
                                -
                              </span>
                            )}
                          </td>
                        )}

                        {/* INTENSYWNOŚĆ */}

                        {false && (
                          <td
                            style={{
                              textAlign: "end",
                              // width: '106px',
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                //background: '#273e52',
                                height: "26px",
                                ...flexEndClass,
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  //background: '#273e52',
                                  height: "26px",
                                  ...flexEndClass,
                                }}
                              >
                                {data?.IntensityPercentage !== null &&
                                avgStats?.IntensityPercentage !== null ? (
                                  getStyledSpan(
                                    data.IntensityPercentage!,
                                    avgStats.IntensityPercentage!
                                  )
                                ) : (
                                  <span
                                    style={{
                                      padding: "0 13px 0 0",
                                      background: "#2a4054",
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        )}

                        {/* WYTRZYMAŁOŚĆ */}

                        {false && (
                          <td
                            style={{
                              textAlign: "end",
                              width: "120px",
                              color: "rgba(248, 248, 248, 0.698)",
                            }}
                          >
                            {data?.EndurancePercentage !== null &&
                            avgStats?.EndurancePercentage !== null ? (
                              getStyledSpan(data.EndurancePercentage!, avgStats.EndurancePercentage!)
                            ) : (
                              <span
                                style={{
                                  padding: "0 13px 0 0",
                                  background: "#2a4054",
                                  width: "168px",
                                  display: "inline-block",
                                  height: "26px",
                                }}
                              >
                                -
                              </span>
                            )}
                          </td>
                        )}
                      </OrderedCells>
                    </tr>
                  </>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HomeTableNew;
